import { ref } from "vue";

import { useAuthStore } from "@/stores/auth";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** SSE events emitted by POST /ai/chat */
interface TextDeltaEvent {
  text: string;
  type: "text_delta";
}

interface UsageEvent {
  cacheReadInputTokens?: number;
  cacheWriteInputTokens?: number;
  inputTokens: number;
  model: string;
  outputTokens: number;
  tier: number;
  type: "usage";
}

interface DoneEvent {
  messageId: string;
  type: "done";
}

interface ErrorEvent {
  code: string;
  message: string;
  type: "error";
}

/**
 * The text streamed so far this round was the model's THINKING before a tool
 * call, not the answer — move it into the visible thinking trail (kept, not
 * wiped) and start a fresh answer buffer. Keeps the live streaming feel without
 * the narration flickering or leaking into the final answer.
 */
interface ThinkingCommitEvent {
  type: "thinking_commit";
}

/**
 * Tool activity — emitted when the assistant runs a query/lookup. `running` is
 * sent before the tool executes, `done` (same `id`) when it finishes, carrying
 * a one-line result summary. Drives the "show the work" activity panel.
 */
interface ToolStatusEvent {
  id: string;
  label: string;
  state: "done" | "running";
  summary?: string;
  tool: string;
  type: "tool_status";
}

/** A normalized activity step the UI renders (running spinner → done check). */
export interface ToolActivityStep {
  done: boolean;
  id: string;
  label: string;
  summary?: string;
  tool: string;
}

type SSEEvent =
  | DoneEvent
  | ErrorEvent
  | TextDeltaEvent
  | ThinkingCommitEvent
  | ToolStatusEvent
  | UsageEvent;

export interface SendMessageParams {
  conversationId?: null | string;
  entityId: string;
  entityType: string;
  question: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const apiBaseUrl = import.meta.env.VITE_API_URL || "";

/** Timeout for the entire stream (5 minutes) */
const STREAM_TIMEOUT_MS = 5 * 60 * 1000;

/** The browser's IANA zone, or undefined when the runtime cannot say (the server then uses UTC). */
function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

/**
 * Reactive composable for SSE streaming from POST /ai/chat.
 *
 * Uses native `fetch()` (not axios) because axios does not support
 * ReadableStream / SSE parsing out of the box.
 */
export function useChatStream() {
  const authStore = useAuthStore();
  const streaming = ref(false);
  const currentText = ref("");
  const error = ref<null | string>(null);
  const conversationId = ref<null | string>(null);
  /** Live tool-activity steps for the current message (cleared on each send). */
  const activitySteps = ref<ToolActivityStep[]>([]);
  /** The model's committed thinking-before-a-tool snippets, in order. Live only
   *  (cleared on each send); shown muted while the answer streams. */
  const thinkingTrail = ref<string[]>([]);

  /** Apply a tool_status event to the activity list (insert on running, mark
   *  done + attach summary on done — matched by id). */
  function applyToolStatus(event: ToolStatusEvent) {
    const existing = activitySteps.value.find((s) => s.id === event.id);
    if (event.state === "running") {
      if (!existing) {
        activitySteps.value = [
          ...activitySteps.value,
          { done: false, id: event.id, label: event.label, tool: event.tool },
        ];
      }
      return;
    }
    // done
    if (existing) {
      existing.done = true;
      existing.summary = event.summary;
      if (event.label) existing.label = event.label;
    } else {
      activitySteps.value = [
        ...activitySteps.value,
        { done: true, id: event.id, label: event.label, summary: event.summary, tool: event.tool },
      ];
    }
  }

  /** Abort controller for the current stream (allows cancellation) */
  let abortController: AbortController | null = null;

  /** Bumped by reset(). The stream it aborts settles a tick later, and must not write its
   *  "Request was cancelled." (or anything else) back into the state reset() just cleared. */
  let epoch = 0;

  /** Numbers each sendMessage. Only the latest may clear `streaming`: an older call aborted by a
   *  newer one (or by reset()) settles later, while the newer stream is still running. */
  let latestRequest = 0;

  /**
   * POST to /ai/chat with the given token and signal. Token is passed in (not
   * read from storage) so the post-refresh retry can use the freshly minted
   * token without racing the localStorage write, mirroring the axios
   * interceptor's pattern in api/axios.ts.
   */
  function postChat(
    params: SendMessageParams,
    token: string,
    signal: AbortSignal
  ): Promise<Response> {
    return fetch(`${apiBaseUrl}/ai/chat`, {
      body: JSON.stringify({
        conversationId: params.conversationId ?? undefined,
        entityId: params.entityId,
        entityType: params.entityType,
        question: params.question,
        // The viewer's zone (RAP-693): "today", the portal metrics windows and lead date filters
        // are read as days where the user sits. The server ignores a zone it does not know.
        tz: browserTimeZone(),
      }),
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      signal,
    });
  }

  /**
   * Send a message and stream the response via SSE.
   *
   * Returns a promise that resolves when the stream ends (done/error).
   * While streaming, `currentText` is updated reactively with each
   * `text_delta` event so the UI can render incrementally.
   */
  async function sendMessage(params: SendMessageParams): Promise<{
    conversationId: null | string;
    fullText: string;
  }> {
    const requestEpoch = epoch;
    const requestId = ++latestRequest;
    // Reset state
    streaming.value = true;
    currentText.value = "";
    error.value = null;
    activitySteps.value = [];
    thinkingTrail.value = [];

    // Cancel any in-flight stream
    if (abortController) {
      abortController.abort();
    }
    // Snapshot the controller for this request. cancel() (or a concurrent
    // sendMessage) can null/replace `abortController` while we're awaiting the
    // refresh below, so the retry must use this local reference.
    const controller = new AbortController();
    abortController = controller;
    const signal = controller.signal;

    // Timeout guard
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, STREAM_TIMEOUT_MS);

    try {
      const initialToken = localStorage.getItem("access_token") || "";
      let response = await postChat(params, initialToken, signal);

      // Token expired — refresh once and retry. This mirrors the axios
      // interceptor's 401 → refresh → retry flow, which `fetch()` bypasses.
      if (response.status === 401) {
        const refreshed = await authStore.refreshAccessToken();
        if (signal.aborted) {
          // User cancelled (or the timeout fired) during the refresh await.
          // Fall through; the AbortError thrown by a no-op fetch would land in
          // the same handler, but skipping the retry avoids a wasted request.
          throw new DOMException("Request was cancelled", "AbortError");
        }
        if (refreshed && authStore.accessToken) {
          response = await postChat(params, authStore.accessToken, signal);
        } else if (!authStore.isAuthenticated) {
          // Refresh failed because the session is gone, not a transient outage.
          // Send the user back to login like the interceptor does.
          const currentPath = window.location.pathname;
          if (!currentPath.startsWith("/auth/")) {
            const fullPath = currentPath + window.location.search;
            window.location.href = `/auth/login?redirect=${encodeURIComponent(fullPath)}`;
          }
        }
      }

      if (!response.ok) {
        const errorBody = await response.text().catch(() => "");
        let errorMsg = `Request failed with status ${response.status}`;
        try {
          const parsed = JSON.parse(errorBody);
          if (parsed.error) errorMsg = parsed.error;
        } catch {
          if (errorBody) errorMsg = errorBody;
        }
        throw new Error(errorMsg);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body reader available");
      }

      const decoder = new TextDecoder();
      let buffer = "";

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          // Keep the last (possibly incomplete) line in the buffer
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;

            const dataStr = line.slice(6).trim();
            if (!dataStr) continue;

            try {
              const event = JSON.parse(dataStr) as SSEEvent;

              switch (event.type) {
                case "text_delta":
                  currentText.value += event.text;
                  break;

                case "thinking_commit":
                  // The text streamed before this tool call was thinking — keep
                  // it in the trail (shown muted) and start a fresh answer buffer.
                  if (currentText.value.trim()) {
                    thinkingTrail.value = [...thinkingTrail.value, currentText.value.trim()];
                  }
                  currentText.value = "";
                  break;

                case "usage":
                  // Usage metadata -- currently we don't surface this in the UI
                  // but it's available if needed in the future
                  break;

                case "done":
                  // The `messageId` field is actually the conversationId
                  conversationId.value = event.messageId;
                  break;

                case "tool_status":
                  applyToolStatus(event);
                  break;

                case "error":
                  error.value = event.message || "An error occurred";
                  break;
              }
            } catch {
              // Non-JSON line, skip
            }
          }
        }

        // Process any remaining buffer
        if (buffer.startsWith("data: ")) {
          const dataStr = buffer.slice(6).trim();
          if (dataStr) {
            try {
              const event = JSON.parse(dataStr) as SSEEvent;
              if (event.type === "text_delta") {
                currentText.value += event.text;
              } else if (event.type === "thinking_commit") {
                if (currentText.value.trim()) {
                  thinkingTrail.value = [...thinkingTrail.value, currentText.value.trim()];
                }
                currentText.value = "";
              } else if (event.type === "done") {
                conversationId.value = event.messageId;
              } else if (event.type === "tool_status") {
                applyToolStatus(event);
              } else if (event.type === "error") {
                error.value = event.message || "An error occurred";
              }
            } catch {
              // skip
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      // If we got NOTHING back — no answer text, no thinking, no tool activity —
      // and no explicit error, treat it as a failed turn. (Don't flag a turn that
      // streamed visible thinking/work-log as an error just because the final
      // answer text was short/empty — that would show reasoning beside a red
      // error.)
      const hadActivity = thinkingTrail.value.length > 0 || activitySteps.value.length > 0;
      if (!currentText.value && !error.value && !hadActivity) {
        error.value = "No response received. Please try again.";
      }

      return {
        conversationId: conversationId.value,
        fullText: currentText.value,
      };
    } catch (err: unknown) {
      if (requestEpoch !== epoch) return { conversationId: null, fullText: "" };
      if (err instanceof DOMException && err.name === "AbortError") {
        error.value = "Request was cancelled.";
      } else {
        error.value = err instanceof Error ? err.message : "An unexpected error occurred";
      }
      return {
        conversationId: conversationId.value,
        fullText: currentText.value,
      };
    } finally {
      clearTimeout(timeoutId);
      if (requestId === latestRequest) streaming.value = false;
      // Only clear if no concurrent sendMessage has replaced our controller.
      if (abortController === controller) {
        abortController = null;
      }
    }
  }

  /**
   * Cancel an in-progress stream.
   */
  function cancel() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  /**
   * Cancel and forget: for a sign-out or account switch, where the partial answer, thinking trail
   * and work log belong to the previous user. `cancel()` alone keeps them (a user stopping their
   * own answer still sees it), and this panel outlives the switch, so they'd render again the next
   * time it shows a loading state.
   */
  function reset() {
    epoch++;
    cancel();
    currentText.value = "";
    thinkingTrail.value = [];
    activitySteps.value = [];
    conversationId.value = null;
    error.value = null;
  }

  return {
    activitySteps,
    cancel,
    conversationId,
    currentText,
    error,
    reset,
    sendMessage,
    streaming,
    thinkingTrail,
  };
}
